import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Typography,
} from "antd";
import {
  EXPORT_CNF_EXPENDITURE_ADD_ENDPOINT,
  EXPORT_CNF_EXPENDITURE_DELETE_ENDPOINT,
  EXPORT_CNF_EXPENDITURE_EDIT_ENDPOINT,
  EXPORT_CNF_EXPENDITURE_LIST_ENDPOINT,
  IMPORT_CNF_EXPENDITURE_ADD_ENDPOINT,
  IMPORT_CNF_EXPENDITURE_DELETE_ENDPOINT,
  IMPORT_CNF_EXPENDITURE_EDIT_ENDPOINT,
  IMPORT_CNF_EXPENDITURE_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData, putData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { NO_PERMISSION_REQUIRED } from "routes/permissions";
import { hasPermission } from "utils/functions";

const IMPORT_CFG = {
  pageTitle: "Import C&F",
  listEndpoint: IMPORT_CNF_EXPENDITURE_LIST_ENDPOINT,
  addEndpoint: IMPORT_CNF_EXPENDITURE_ADD_ENDPOINT,
  editEndpoint: IMPORT_CNF_EXPENDITURE_EDIT_ENDPOINT,
  deleteEndpoint: IMPORT_CNF_EXPENDITURE_DELETE_ENDPOINT,
  deleteConfirm: "Are you sure you want to delete this Import C&F item?",
  showAmount: true,
};

const EXPORT_CFG = {
  pageTitle: "Export C&F",
  listEndpoint: EXPORT_CNF_EXPENDITURE_LIST_ENDPOINT,
  addEndpoint: EXPORT_CNF_EXPENDITURE_ADD_ENDPOINT,
  editEndpoint: EXPORT_CNF_EXPENDITURE_EDIT_ENDPOINT,
  deleteEndpoint: EXPORT_CNF_EXPENDITURE_DELETE_ENDPOINT,
  deleteConfirm: "Are you sure you want to delete this Export C&F item?",
};

function CnfExpenditureSettingsView({ config }) {
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [form] = Form.useForm();
  const { Title } = Typography;

  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  const fetchList = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${config.listEndpoint}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        const dataWithKeys = data.map((item, index) => ({
          ...item,
          key: item.id || `cnf-${index}`,
        }));

        setTableData(dataWithKeys);
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultCurrent: response?.data?.data?.from || 1,
          defaultPageSize: response?.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
        setLoading(false);
      } else {
        setFetchError("Could not load this list.");
        setLoading(false);
      }
    },
    [config.listEndpoint, currentPage],
  );

  useEffect(() => {
    fetchList(filterData);
  }, [refresh, filterData, fetchList]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = useCallback(() => {
    form.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  }, [form]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteRow = useCallback(
    async (id) => {
      Modal.confirm({
        title: "Confirm Deletion",
        content: config.deleteConfirm,
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          const response = await deleteData(
            `${config.deleteEndpoint}/${id}`,
          );

          if (
            response &&
            (response?.code === 200 || response?.status === 200)
          ) {
            setLoading(false);
            refetch();
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    },
    [config.deleteConfirm, config.deleteEndpoint, refetch],
  );

  const handleSubmit = useCallback(
    async (formValues) => {
      setLoading(true);
      const isEditMode = !!formValues.id;

      const bodyData = {
        name: formValues?.name,
        ...(config.showAmount && { amount: formValues?.amount }),
      };

      let response;

      if (isEditMode) {
        response = await putData(
          `${config.editEndpoint}/${formValues.id}`,
          bodyData,
        );
      } else {
        response = await postData(config.addEndpoint, bodyData);
      }

      if (response && (response?.code === 200 || response?.status === 200)) {
        notification.success({
          message: "Success",
          description: response?.message ? response?.message : "",
          placement: "topRight",
        });
        onCloseModal();
        refetch();
      }
      setLoading(false);
    },
    [config.addEndpoint, config.editEndpoint, config.showAmount, onCloseModal, refetch],
  );

  const openModalForEdit = useCallback(
    (record) => {
      setIsEdit(true);
      form.setFieldsValue({
        id: record.id,
        name: record.name,
        ...(config.showAmount && {
          amount:
            record.amount !== undefined && record.amount !== null
              ? Number(record.amount)
              : undefined,
        }),
      });
      setIsModalOpen(true);
    },
    [form, config.showAmount],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        width: config.showAmount ? "30%" : "35%",
        key: "name",
        ellipsis: true,
      },
      ...(config.showAmount
        ? [
            {
              title: "Amount (Taka)",
              dataIndex: "amount",
              width: "20%",
              key: "amount",
              render: (amount) =>
                amount !== undefined && amount !== null
                  ? Number(amount).toLocaleString()
                  : "0",
            },
          ]
        : []),
      {
        title: "Action",
        key: "action",
        width: "15%",
        render: (_, record) => (
          <div
            className="action-buttons"
            style={{ display: "flex", gap: "8px" }}
          >
            {hasPermission([NO_PERMISSION_REQUIRED]) && (
              <Button
                type="primary"
                size="small"
                onClick={() => openModalForEdit(record)}
                className="edit-button"
                style={{ backgroundColor: "#1890ff" }}
              >
                Edit
              </Button>
            )}
            {hasPermission([NO_PERMISSION_REQUIRED]) && (
              <Button
                type="primary"
                danger
                size="small"
                onClick={() => deleteRow(record.id)}
                className="delete-button"
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [openModalForEdit, deleteRow, config.showAmount],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={fetchList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, fetchList],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([NO_PERMISSION_REQUIRED])}
                addButtonText={
                  hasPermission([NO_PERMISSION_REQUIRED])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>{config.pageTitle}</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => fetchList(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isEdit ? (
              <span style={{ color: "#1890ff" }}>Edit {config.pageTitle}</span>
            ) : (
              <span style={{ color: "#52c41a" }}>
                Create {config.pageTitle}
              </span>
            )}
          </div>
        }
        onCancel={onCloseModal}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
        destroyOnClose
        width={520}
        centered
        bodyStyle={{ padding: "24px" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{}}
          requiredMark="optional"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: "Name is required",
                  },
                  {
                    max: 255,
                    message: "Name cannot exceed 255 characters",
                  },
                ]}
              >
                <Input
                  placeholder="Enter name"
                  autoFocus
                  autoComplete="off"
                  style={{ borderRadius: "4px" }}
                />
              </Form.Item>
              {config.showAmount && (
                <Form.Item
                  name="amount"
                  label="Amount (Taka)"
                  rules={[
                    {
                      required: true,
                      message: "Amount is required",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Enter amount"
                    min={0}
                    step={1}
                    style={{ width: "100%", borderRadius: "4px" }}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
          <Row
            gutter={16}
            className="form-actions"
            style={{ marginTop: "12px" }}
          >
            <Col span={12}>
              <Button
                type="default"
                danger
                style={{ width: "100%", borderRadius: "4px" }}
                onClick={onCloseModal}
              >
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%", borderRadius: "4px" }}
                loading={loading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export function ImportCnfExpenditure() {
  return <CnfExpenditureSettingsView config={IMPORT_CFG} />;
}

export function ExportCnfExpenditure() {
  return <CnfExpenditureSettingsView config={EXPORT_CFG} />;
}
